// ===== FUNCTIONS PURAS (para tree-shaking máximo) =====

/**
 * Remove acentos de uma string
 */
export function removerAcentos(texto: string): string {
  return texto?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Converte para formato de nome próprio (primeira letra maiúscula)
 */
export function formatarNomeProprio(texto: string): string {
  return texto?.toLowerCase().replace(/\b\w/g, (letra) => letra.toUpperCase());
}

/**
 * Remove todos os caracteres não numéricos
 */
export function apenasNumeros(texto: string): string {
  return texto?.replace(/\D/g, '');
}

/**
 * Trunca texto com reticências
 */
export function truncar(texto: string, limite: number): string {
  return texto?.length > limite ? `${texto.substring(0, limite)}...` : texto;
}

/**
 * Gera slug amigável para URL
 */
export function gerarSlug(texto: string): string {
  return removerAcentos(texto)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/**
 * Verifica se string está vazia ou contém apenas espaços
 */
export function estaVazia(texto: string | null | undefined): boolean {
  return !texto || texto.trim().length === 0;
}

/**
 * Converte primeira letra em maiúscula
 */
export function capitalize(texto: string): string {
  return texto?.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
}

/**
 * Converte para camelCase
 */
export function toCamelCase(texto: string): string {
  return texto
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
}

/**
 * Converte para kebab-case
 */
export function toKebabCase(texto: string): string {
  return texto?.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Converte para snake_case
 */
export function toSnakeCase(texto: string): string {
  return texto?.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

/**
 * Converte para PascalCase
 */
export function toPascalCase(texto: string): string {
  return texto?.replace(/(?:^|[^a-zA-Z0-9]+)([a-zA-Z0-9])/g, (_, char) =>
    char.toUpperCase()
  );
}

/**
 * Remove espaços duplicados e normaliza espaçamento
 */
export function normalizarEspacos(texto: string): string {
  return texto?.replace(/\s+/g, ' ').trim();
}

/**
 * Conta palavras em um texto
 */
export function contarPalavras(texto: string): number {
  return texto
    ?.trim()
    .split(/\s+/)
    .filter((palavra) => palavra.length > 0).length;
}

/**
 * Extrai apenas letras de um texto
 */
export function apenasLetras(texto: string): string {
  return texto?.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/g, '');
}

/**
 * Extrai apenas letras e números
 */
export function apenasLetrasNumeros(texto: string): string {
  return texto?.replace(
    /[^a-zA-Z0-9áàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/g,
    ''
  );
}

/**
 * Inverte uma string
 */
export function inverter(texto: string): string {
  return texto?.split('').reverse().join('');
}

/**
 * Remove caracteres especiais mantendo apenas letras, números e espaços
 */
export function removerCaracteresEspeciais(texto: string): string {
  return texto?.replace(
    /[^a-zA-Z0-9áàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g,
    ''
  );
}

/**
 * Verifica se string contém apenas números
 */
export function somenteNumeros(texto: string): boolean {
  return /^\d+$/.test(texto);
}

/**
 * Verifica se string contém apenas letras
 */
export function somenteLetras(texto: string): boolean {
  return /^[a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]+$/.test(texto);
}

/**
 * Padroniza um texto removendo acentos e convertendo para minúsculas
 */
export function padronizar(texto: string): string {
  return removerAcentos(texto).toLowerCase().trim();
}

/**
 * Formata um número de telefone brasileiro adicionando o 9
 */
export function formatarTelefoneComNoveSem55(telefone: string): string {
  if (telefone.length === 11) {
    return telefone;
  }
  const telefoneSemDDD = telefone.substring(4, telefone.length);
  const ddd = telefone.substring(2, 4);

  if (telefoneSemDDD.length === 8) {
    return ddd + '9' + telefoneSemDDD;
  }

  return ddd + telefoneSemDDD;
}

// ===== CLASSE ESTÁTICA (para organização e compatibilidade) =====

/**
 * Classe estática com todos os utilitários de string
 * Use quando preferir organização por namespace: StringUtils.removerAcentos()
 */
export class StringUtils {
  static removerAcentos = removerAcentos;
  static formatarNomeProprio = formatarNomeProprio;
  static apenasNumeros = apenasNumeros;
  static truncar = truncar;
  static gerarSlug = gerarSlug;
  static estaVazia = estaVazia;
  static capitalize = capitalize;
  static toCamelCase = toCamelCase;
  static toKebabCase = toKebabCase;
  static toSnakeCase = toSnakeCase;
  static toPascalCase = toPascalCase;
  static normalizarEspacos = normalizarEspacos;
  static contarPalavras = contarPalavras;
  static apenasLetras = apenasLetras;
  static apenasLetrasNumeros = apenasLetrasNumeros;
  static inverter = inverter;
  static removerCaracteresEspeciais = removerCaracteresEspeciais;
  static somenteNumeros = somenteNumeros;
  static somenteLetras = somenteLetras;
  static padronizar = padronizar;
  static formatarTelefoneComNoveSem55 = formatarTelefoneComNoveSem55;
}
